import React, {useEffect, useState} from "react";
import {GridColDef, GridPaginationModel} from '@mui/x-data-grid-pro';
import {useLazyQuery, useMutation} from "@apollo/client";
import _ from "lodash";
import {paginationLimit} from "../../config/paginationLimit";
import {Typography} from "@mui/joy";
import Table from "../Table";
import {useTranslation} from "react-i18next";
import {GridViewList} from "../../graphql/queries/gridViewList";
import getFilter from "../../utils/getFilter";
import {ActionsMenu, getFilterParams, getInitialPagination} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {DeleteRounded, EditRounded} from "@mui-symbols-material/w300";
import {toast} from "react-toastify";
import {DeleteGridView} from "../../graphql/mutations/deleteGridView";
import ConfirmDeleteModal from "../ConfirmDeleteModal";

interface Props {
    height: number,
    onChangePagination?: (newPaginationModel: GridPaginationModel) => void,
    searchParams?: { query: string, fields: string[] }
    urlParams: URLSearchParams,
    filterKeys?: string[],
    onOpenGridView: (gridViewId: string) => void;
    onEditGridView: (gridViewId: string) => void;
}

const GridViewTable = ({
                           height,
                           onChangePagination,
                           searchParams,
                           urlParams,
                           filterKeys,
                           onOpenGridView,
                           onEditGridView,
                        }: Props) => {
    const {t} = useTranslation();


    const [paginationModel, setPaginationModel] = useState(getInitialPagination(paginationLimit, urlParams));

    useEffect(() => {
        setPaginationModel({
            page: getInitialPagination(paginationLimit, urlParams).page,
            pageSize: getInitialPagination(paginationLimit, urlParams).pageSize
        })
    }, [searchParams])

    useEffect(() => {
        if (onChangePagination) {
            onChangePagination(paginationModel)
        }
    }, [paginationModel])

    const filter = getFilterParams(filterKeys, urlParams);

    const [deleteGridViewMutation, deleteGridViewResult] = useMutation(DeleteGridView)
    const [deleteTarget, setDeleteTarget] = useState<{ id: string, name: string } | null>(null);
    const [fetchGridViewList, gridViewListQuery] = useLazyQuery(GridViewList, {
        variables: {
            search: searchParams ? {
                query: searchParams.query,
                fields: searchParams.fields
            } : {},
            pagination: {
                start: paginationModel.page * paginationModel.pageSize,
                limit: paginationModel.pageSize
            },
            filter: {
                clientId: getFilter(filter.clientId, "equals")
            }
        },
        fetchPolicy: "network-only"
    });
    const gridViewListData = _.get(gridViewListQuery, 'data.gridViewList.gridViews', []);
    const totalGridViews = _.get(gridViewListQuery, 'data.gridViewList.total',)

    useEffect(() => {
        fetchGridViewList();
    }, [fetchGridViewList]);

    const onDeleteGridView = () => {
        if (!deleteTarget) return;
        deleteGridViewMutation({
            variables: {
                id: deleteTarget.id,
            },
            refetchQueries: ["GridViewList"]
        })
            .then((res) => {
                if (res.data?.deleteGridView?.success && res.data?.deleteGridView?.message) {
                    toast.success(res.data.deleteGridView.message);
                }
                setDeleteTarget(null);
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t))
                setDeleteTarget(null);
            })
    }

    const columns:GridColDef[] = [
        {
            field: 'name',
            headerName: t('Name'),
            flex: 1,
            renderCell: (params) => {
                const gridViewId = _.get(params, 'row.id');
                const title = _.get(params, 'row.title')

                return <Typography fontWeight={"bold"} sx={{cursor: 'pointer'}} color={"primary"}
                                   onClick={() => onOpenGridView(gridViewId)}>
                    {title}
                </Typography>;
            },
            display: "flex",
            disableColumnMenu: true
        },
        {
            field: 'client',
            headerName: t('Company'),
            flex: 1,
            valueGetter: (value, row) => {
                return _.get(value, 'name');
            },
            disableColumnMenu: true
        },
        {
            field: 'assetCount',
            headerName: t('Asset Count'),
            width: 150,
            disableColumnMenu: true
        },
        {
            field: 'rotationTime',
            headerName: t('Rotation Time'),
            width: 150,
            valueGetter: (value, row) => {
                return value ? `${value}s` : '-';
            },
            disableColumnMenu: true
        },
        {
            field: 'actions',
            headerName: t('Actions'),
            width: 80,
            disableColumnMenu: true,
            align: "center",
            renderCell: (params) => {
                const gridViewId = _.get(params, 'row.id')
                const gridViewTitle = _.get(params, 'row.title')

                const actionItems: { item: React.ReactNode, onClick: () => void, active: boolean }[] = [
                    {
                        item: <><EditRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Edit')}</>,
                        onClick: () => onEditGridView(gridViewId),
                        active: true,
                    },
                    {
                        item: <><DeleteRounded fontSize={'inherit'} sx={{mr: '4px'}}/>{t('Delete')}</>,
                        onClick: () => setDeleteTarget({id: gridViewId, name: gridViewTitle}),
                        active: true,
                    },
                ]

                return <ActionsMenu items={actionItems} />
            }
        }
    ]

    return (
        <div style={{width: "100%", height: height}}>
            <Table
                loading={gridViewListQuery.loading}
                data={gridViewListData}
                rowCount={totalGridViews}
                columns={columns}
                pagination={true}
                paginationMode="server"
                paginationModel={paginationModel}
                pageSizeOptions={[paginationLimit]}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSorting
                disableRowSelectionOnClick
                disableColumnResize
                disableColumnReorder
                disableColumnPinning
            />
            {deleteTarget && (
                <ConfirmDeleteModal
                    open={!!deleteTarget}
                    title={t('Delete grid view')}
                    message={t('Are you sure you want to delete the grid view {{name}} including all of its grids? This action cannot be undone.', {name: deleteTarget.name})}
                    loading={deleteGridViewResult.loading}
                    onConfirm={onDeleteGridView}
                    onClose={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}

export default GridViewTable;



